// Upcoming occurrences of recurring tasks in calendars. A recurring task is one stored
// task; the server computes the occurrences after it (GET /api/tasks/occurrences, backend
// utils/recurrence.py) and none of them is stored. They are shown read-only and open the
// series' current task.
import { useCallback, useState } from 'react';
import { useTaskOccurrencesQuery } from '../hooks/queries';

/** The occurrences for the dates a FullCalendar shows; pass onDatesSet to its datesSet. */
export const useCalendarOccurrences = () => {
  const [range, setRange] = useState(null);
  const onDatesSet = useCallback((dateInfo) => {
    const next = { start: dateInfo.start.toISOString(), end: dateInfo.end.toISOString() };
    setRange((prev) => (prev && prev.start === next.start && prev.end === next.end ? prev : next));
  }, []);
  const { data = [] } = useTaskOccurrencesQuery(range);
  return { occurrences: data, onDatesSet };
};

/**
 * FullCalendar events for the occurrences of the tasks a calendar shows (so its filters
 * apply). timedOnly: only occurrences with a time slot (calendars of time blocks).
 */
export const occurrenceEvents = (occurrences, tasks, { colorsFor, projectNameOf = () => undefined, timedOnly = false } = {}) => {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  return occurrences
    .filter((o) => byId.has(o.task_id) && (!timedOnly || (o.scheduled_start && o.scheduled_end)))
    .map((o) => {
      const task = byId.get(o.task_id);
      const colors = colorsFor(task);
      const start = o.scheduled_start || o.due_date;
      const end = o.scheduled_end
        || new Date(new Date(o.due_date).getTime() + (o.duration || 60) * 60000).toISOString();
      return {
        id: `occurrence-${o.task_id}-${o.occurrence_number}`,
        title: o.title || task.title || '',
        start,
        end,
        allDay: !o.scheduled_start,
        editable: false,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: colors.text,
        classNames: ['opacity-60'],
        extendedProps: {
          task, occurrence: o, isOccurrence: true, priority: task.priority,
          duration: o.duration || task.duration, projectName: projectNameOf(task.project_id),
        },
      };
    });
};
